import type { AvatarProps } from '@archipro-design/aria';
import { HotButtons } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    useStyles,
    pxArrayToRem,
    Container,
    Divider,
    useTheme,
} from '@archipro-design/aria';

import { Link } from '@remix-run/react';
import { TileGrid } from '@modules/category/component/tile-grid/TileGrid';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';

import * as S from '@modules/professional/component/shared/PaginatedStyles.style';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import type {
    DefaultElasticSearchResults,
    ProfessionalTileViewModel,
} from '@modules/professional/type';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import FilterDialogMobile from '@modules/professional/component/shared/FilterDialog.mobile';
import { useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useToggle } from 'ahooks';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useAppData, useMatchesHandleData } from '~/modules/root/hook';
import type { ElasticSearchCategories } from '~/modules/search/type';

import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Container as PageLayoutContainer } from '@modules/professional/component/page-layout/PageLayout.style';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import { getAdminCaption } from '~/modules/directory/util/get-admin-caption';
import AdminCaption from '~/modules/tile/component/admin-caption/AdminCaption';
import type { SyntheticEvent } from 'react';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import MessageModal from '~/modules/home/component/common/MessageModal';
import { canHomePin } from '~/modules/home/util/home-pin-helpers';

export interface PaginatedProfessionalsProp
    extends DefaultElasticSearchResults {
    professionalTiles: ProfessionalTileViewModel[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedNetwork: React.FC<PaginatedProfessionalsProp> = ({
    pagination,
    professionalTiles,
    searchResult,
    filterStates,
    disableTileLink,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const hasPagination = pagination.totalPages !== 1;
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const { favouriteItem } = useSaveToDesignBoard('ProfessionalPin');
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const renderTileAs = disableTileLink ? 'div' : Link;

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] = useToggle(
        !disableSort
    );
    const gridColumns = desktop ? 3 : 1;
    const tracker = useTracker();
    const tileLen = professionalTiles.length;

    const { rootGrants } = useAppData();
    const canEditAdmin = rootGrants.editProfile;
    const professionalIDs = professionalTiles.map(
        (professional) => professional.professionalID
    );
    const adminTileMap = useItemAdminData(canEditAdmin, professionalIDs);

    const [pinErrorMessage, setPinErrorMessage] = useState<string | null>(null);
    const { toggleHomePin } = useToggleHomePin(canEditAdmin, (error) => {
        if (error.Message) {
            setPinErrorMessage(error.Message);
        }
    });

    const tiles = professionalTiles.map((professional, index) => {
        const { tile, link } = professional;
        const memberSince = mobile
            ? { styles: { display: 'none' } }
            : tile.memberSince;

        const tilePaddingBottom =
            desktop &&
            !hasPagination &&
            tileLen - (tileLen % gridColumns) <= index &&
            tileLen - index <= gridColumns
                ? 0
                : 14;

        const {
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            ...tileRest
        } = tile;

        const tileProps = {
            ...tileRest,
            memberSince,
        };

        const adminItem = adminTileMap[professional.professionalID];

        const hotButtons = getHotButtons({
            item: tile,
            itemType: 'professional',
            appDisplayMode,
            canEditAdmin,
            canEdit: Boolean(tile.canEdit),
            onEditLink: tile.bmEditorLink,
            editOnSameTab: disableTileLink || isBMPreview,
            pinned: !!adminItem?.homePinned,
            onPinClick: canHomePin(rootGrants)
                ? () => {
                      if (!adminItem) {
                          return;
                      }
                      toggleHomePin(
                          professional.professionalID,
                          !adminItem.homePinned
                      );
                  }
                : undefined,
        });

        const adminCaption = getAdminCaption({
            appDisplayMode,
            item: adminItem,
            canEditAdmin,
        });

        const imageProps = tile.image.map((image) => {
            return {
                src: image,
                width: '100%',
                height: '100%',
                sizes: getImagesSizes(434),
                alt: tile?.title,
            };
        });

        const linkProps = !disableTileLink
            ? {
                  to: link,
                  prefetch: 'intent',
                  target: '_parent',
              }
            : {};

        return (
            <ProfessionalTile
                key={link}
                as={renderTileAs}
                {...linkProps}
                {...tileProps}
                image={imageProps}
                logo={{
                    image: {
                        src:
                            typeof tile.logo === 'object'
                                ? ((tile.logo as unknown as AvatarProps)
                                      .image as string)
                                : '',
                        alt: `${tile.title} professional logo`,
                        loading: index < gridColumns ? 'eager' : 'lazy',
                    },
                    variables: (tile.logo as unknown as AvatarProps).variables,
                }}
                size={
                    !desktop ? 'small' : !filterDrawerOpen ? 'large' : 'medium'
                }
                variant={'04'}
                onFavourite={(e) => {
                    favouriteItem.onClick(e, professional.professionalID);
                }}
                isFavourited={favouriteItem.favourited(
                    professional.professionalID
                )}
                onClick={(e: SyntheticEvent) => {
                    if (isBMPreview) {
                        // prevent default to avoid navigating to public page, onTileClick should handle the navigation in BM
                        e.preventDefault();
                    }
                    tracker.log('ProfessionalNetworkClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                    });
                    if (
                        (isBMPreview || disableTileLink) &&
                        Boolean(tile.canEdit)
                    ) {
                        visitBMEditor(tile, true);
                    }
                }}
                variables={{
                    tilePadding: pxArrayToRem([14, 14, tilePaddingBottom, 14]),
                    ...(!desktop && {
                        dynamicSizingWidth: 414,
                        dynamicSizingGapWidth: 0,
                        tilePadding: 0,
                        avatarMargin: 18,
                    }),
                }}
                impression={{
                    type: 'LibraryItem_Directory',
                    itemID: professional.professionalID,
                    professionalID: professional.professionalID,
                }}
                topActions={
                    hotButtons ? <HotButtons items={hotButtons} /> : undefined
                }
                hideFavourite={disableSaveToDesignBoard}
                adminCaption={
                    adminCaption ? (
                        <AdminCaption adminCaption={adminCaption} />
                    ) : undefined
                }
            />
        );
    });

    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const totalCount = searchResult?.nbHits ?? 0;

    const navFilterButton = {
        children: `Filters`,
        transparent: true,
        onClick: () => setOpenFilterDialog(true),
    };

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<ElasticSearchCategories>(
        'category',
        'project'
    );

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <Container>
            {desktop && (
                <div className={css(PageLayoutContainer)}>
                    <Divider
                        size={2}
                        variables={{
                            dividerPadding: 0,
                            dividerPrimaryColor: siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
            <div className={css(S.Wrap)}>
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <div className={css(S.FilterSection)}>
                                <CategoryFilterNav
                                    category="professional"
                                    searchFilters={searchFilters}
                                />
                            </div>
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                    content={
                        <>
                            <div className={css(S.FilterTogglesSection)}>
                                <SearchFilter
                                    searchFilters={searchFilters}
                                    filterButton={navFilterButton}
                                    initialStates={filterStates}
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                    disableSort={disableSort}
                                />
                            </div>
                            {searchFilters.appliedFilters &&
                                searchFilters.appliedFilters.length > 0 && (
                                    <AppliedFilters
                                        filterItems={
                                            searchFilters.appliedFilters
                                        }
                                        resetFilters={() =>
                                            searchFilters.resetFilters(
                                                resetFilterStates(
                                                    searchFilters.filterStates,
                                                    [
                                                        ...ignoreFilterKeys,
                                                        'category',
                                                    ]
                                                )
                                            )
                                        }
                                    />
                                )}
                            <div className={css(S.ResultSection)}>
                                {!tiles.length ? (
                                    <NoSearchResults
                                        resultsType={'professionals'}
                                    />
                                ) : (
                                    <TileGrid
                                        items={tiles}
                                        columns={gridColumns}
                                        pagination={pagination}
                                        paginationItem={'Professionals'}
                                        onPageChange={(page: number) => {
                                            tracker.log(
                                                'ProfessionalNetworkPageChange',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                    data: {
                                                        ExtraData: page,
                                                    },
                                                }
                                            );
                                        }}
                                        type="professionals"
                                    />
                                )}
                            </div>
                        </>
                    }
                />
                {!disableSaveToDesignBoard && favouriteItem.target && (
                    <SaveToDesignBoard {...favouriteItem} />
                )}
                <FilterDialogMobile
                    openFilterDialog={openFilterDialog}
                    itemCount={totalCount}
                    onClose={() => setOpenFilterDialog(false)}
                    filterComponent={<div>network-filters goes here</div>}
                />
                {pinErrorMessage && (
                    <MessageModal
                        title={'Warning'}
                        content={pinErrorMessage}
                        open={true}
                        onConfirm={() => setPinErrorMessage(null)}
                    />
                )}
            </div>
        </Container>
    );
};
